'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { CheckCircle, Flag, ExternalLink, ChevronUp, ChevronDown, Loader2, RefreshCw } from 'lucide-react';
import Link from 'next/link';
import BadgeTierChip, { BadgeTier } from '@/components/ui/BadgeTierChip';
import VerificationBadge, { VerificationStatus } from '@/components/ui/VerificationBadge';
import { toast } from 'sonner';

interface ExpertRow {
  id: string;
  pseudonym: string;
  domainTags: string[];
  badgeTier: BadgeTier;
  verificationStatus: VerificationStatus;
  experienceTier: string;
  averageRating: number;
  ratingCount: number;
  hourlyRate: number;
  isLive: boolean;
  submittedAt: string;
  webVerifySummary?: string;
  cvReady?: boolean;
  isMock?: boolean;
}

const mockExperts: ExpertRow[] = [
  { id: 'ep-001', pseudonym: 'Advisor #X4Q', domainTags: ['Consumer Goods', 'Retail Strategy', 'Operations'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'PRINCIPAL', averageRating: 0, ratingCount: 0, hourlyRate: 25000, isLive: false, submittedAt: '2026-06-06', cvReady: false, isMock: true },
  { id: 'ep-002', pseudonym: 'Advisor #M2W', domainTags: ['Healthcare', 'Regulatory Affairs', 'MedTech'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'EXPERT', averageRating: 0, ratingCount: 0, hourlyRate: 42000, isLive: false, submittedAt: '2026-06-05', cvReady: false, isMock: true },
  { id: 'ep-003', pseudonym: 'Advisor #P7L', domainTags: ['Private Equity', 'Portfolio Operations', 'Value Creation'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'SENIOR', averageRating: 0, ratingCount: 0, hourlyRate: 18000, isLive: false, submittedAt: '2026-06-05', cvReady: false, isMock: true },
  { id: 'ep-004', pseudonym: 'Advisor #T9N', domainTags: ['Digital Transformation', 'ERP', 'Change Management'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'PRINCIPAL', averageRating: 0, ratingCount: 0, hourlyRate: 32000, isLive: false, submittedAt: '2026-06-04', cvReady: false, isMock: true },
  { id: 'ep-005', pseudonym: 'Advisor #C1H', domainTags: ['Logistics', 'Last-Mile Delivery', 'Supply Chain'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'SENIOR', averageRating: 0, ratingCount: 0, hourlyRate: 16500, isLive: false, submittedAt: '2026-06-04', cvReady: false, isMock: true },
  { id: 'ep-006', pseudonym: 'Advisor #J5R', domainTags: ['Real Estate', 'Capital Markets', 'REIT'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'EXPERT', averageRating: 0, ratingCount: 0, hourlyRate: 55000, isLive: false, submittedAt: '2026-06-03', cvReady: false, isMock: true },
  { id: 'ep-007', pseudonym: 'Advisor #V8D', domainTags: ['EdTech', 'Curriculum Design', 'Learning & Development'], badgeTier: 'NONE', verificationStatus: 'PENDING', experienceTier: 'SENIOR', averageRating: 0, ratingCount: 0, hourlyRate: 12000, isLive: false, submittedAt: '2026-06-03', cvReady: false, isMock: true },
  { id: 'ep-008', pseudonym: 'Advisor #K9M', domainTags: ['FMCG', 'Go-to-Market', 'Brand Strategy'], badgeTier: 'GOLD', verificationStatus: 'WEB_CHECKED', experienceTier: 'PRINCIPAL', averageRating: 4.4, ratingCount: 18, hourlyRate: 22000, isLive: true, submittedAt: '2026-04-10', webVerifySummary: 'Look for LinkedIn profiles showing senior marketing or commercial leadership at multinational FMCG companies, with tenure of 10+ years at the regional or national director level. Search for published articles on brand architecture or distribution strategy in Indian or Southeast Asian trade publications. Check for speaker credits at industry forums such as CII or FICCI marketing conferences. Membership in bodies like the Marketing Society of India or equivalent regional associations is a strong corroborating signal.', cvReady: true, isMock: true },
  { id: 'ep-009', pseudonym: 'Advisor #R3T', domainTags: ['M&A', 'Due Diligence', 'Financial Services'], badgeTier: 'SILVER', verificationStatus: 'WEB_CHECKED', experienceTier: 'EXPERT', averageRating: 4.1, ratingCount: 7, hourlyRate: 35000, isLive: true, submittedAt: '2026-03-22', webVerifySummary: 'Verify through LinkedIn for senior roles at investment banks, PE firms, or Big 4 transaction advisory practices. Look for published deal tombstones or transaction announcements mentioning advisory roles. Check for authored pieces in financial media on M&A trends in financial services. CFA or equivalent chartered qualification visible in credentials is a meaningful signal. Membership in AIMA or CII finance committees adds further credibility.', cvReady: true, isMock: true },
  { id: 'ep-010', pseudonym: 'Advisor #B6F', domainTags: ['Manufacturing', 'Supply Chain', 'Lean Operations'], badgeTier: 'BRONZE', verificationStatus: 'PENDING', experienceTier: 'SENIOR', averageRating: 3.7, ratingCount: 2, hourlyRate: 14000, isLive: true, submittedAt: '2026-02-14', cvReady: false, isMock: true },
];

type SortKey = 'submittedAt' | 'hourlyRate' | 'averageRating' | 'ratingCount';
type SortDir = 'asc' | 'desc';

interface AdminExpertTableProps {
  searchQuery: string;
  statusFilter: string;
}

export default function AdminExpertTable({ searchQuery, statusFilter }: AdminExpertTableProps) {
  const [experts, setExperts] = useState<ExpertRow[]>(mockExperts);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('submittedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchLiveExperts = async () => {
    setLiveLoading(true);
    setLiveError(null);
    try {
      const res = await fetch('/api/admin/experts');
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      const { data } = await res.json();
      if (!Array.isArray(data)) return;

      const liveRows: ExpertRow[] = data.map((row: any) => ({
        id: row.id,
        pseudonym: row.pseudonym ?? '—',
        domainTags: row.domain_tags ?? [],
        badgeTier: (row.badge_tier ?? 'NONE') as BadgeTier,
        verificationStatus: (row.verification_status ?? 'PENDING') as VerificationStatus,
        experienceTier: row.experience_tier ?? 'SENIOR',
        averageRating: Number(row.average_rating ?? 0),
        ratingCount: Number(row.rating_count ?? 0),
        hourlyRate: Number(row.hourly_rate ?? 0),
        isLive: Boolean(row.is_live),
        submittedAt: row.submitted_at ? row.submitted_at.slice(0, 10) : row.created_at?.slice(0, 10) ?? '',
        webVerifySummary: row.web_verify_summary ?? undefined,
        cvReady: Boolean(row.raw_cv_text),
        isMock: false,
      }));

      // Merge: keep all mock rows, append live rows whose pseudonym isn't already in mock set
      const mockPseudonyms = new Set(mockExperts.map((e) => e.pseudonym));
      const newLiveRows = liveRows.filter((r) => !mockPseudonyms.has(r.pseudonym));
      setExperts([...mockExperts, ...newLiveRows]);
    } catch (err: any) {
      setLiveError(err?.message ?? 'Failed to load live profiles');
    } finally {
      setLiveLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveExperts();
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleApprove = async (id: string, pseudonym: string, isMock?: boolean) => {
    setActionLoading(id);
    if (isMock) {
      await new Promise((r) => setTimeout(r, 1000));
      setExperts((prev) =>
        prev.map((e) => e.id === id ? { ...e, isLive: true, verificationStatus: 'WEB_CHECKED' as VerificationStatus } : e)
      );
      setActionLoading(null);
      toast.success(`${pseudonym} approved and set live`);
      return;
    }
    try {
      const res = await fetch('/api/admin/experts', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action: 'approve' }),
      });
      if (!res.ok) throw new Error('Update failed');
      setExperts((prev) =>
        prev.map((e) => e.id === id ? { ...e, isLive: true, verificationStatus: 'WEB_CHECKED' as VerificationStatus } : e)
      );
      toast.success(`${pseudonym} approved and set live`);
    } catch {
      toast.error('Failed to approve expert');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFlag = async (id: string, pseudonym: string, isMock?: boolean) => {
    setActionLoading(id);
    if (isMock) {
      await new Promise((r) => setTimeout(r, 800));
      setExperts((prev) =>
        prev.map((e) => e.id === id ? { ...e, verificationStatus: 'FLAGGED' as VerificationStatus } : e)
      );
      setActionLoading(null);
      toast.error(`${pseudonym} flagged for review`);
      return;
    }
    try {
      const res = await fetch('/api/admin/experts', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action: 'flag' }),
      });
      if (!res.ok) throw new Error('Update failed');
      setExperts((prev) =>
        prev.map((e) => e.id === id ? { ...e, verificationStatus: 'FLAGGED' as VerificationStatus } : e)
      );
      toast.error(`${pseudonym} flagged for review`);
    } catch {
      toast.error('Failed to flag expert');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    return experts
      .filter((e) => {
        const matchesSearch = !searchQuery ||
          e.pseudonym.toLowerCase().includes(searchQuery.toLowerCase()) ||
          e.domainTags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesStatus = statusFilter === 'ALL' || e.verificationStatus === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        let aVal: string | number = a[sortKey];
        let bVal: string | number = b[sortKey];
        if (typeof aVal === 'string') aVal = aVal.localeCompare(bVal as string);
        else aVal = (aVal as number) - (bVal as number);
        return sortDir === 'asc' ? (aVal as number) : -(aVal as number);
      });
  }, [experts, searchQuery, statusFilter, sortKey, sortDir]);

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronDown size={12} className="text-border" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  return (
    <div className="surface-elevated overflow-hidden">
      {/* Live data status bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-muted/20">
        <div className="flex items-center gap-2">
          {liveLoading ? (
            <>
              <Loader2 size={12} className="text-primary animate-spin" />
              <span className="text-[10px] font-mono-data text-muted-foreground">Loading live profiles…</span>
            </>
          ) : liveError ? (
            <>
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-[10px] font-mono-data text-red-400">Live data error: {liveError}</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-secondary" />
              <span className="text-[10px] font-mono-data text-muted-foreground">
                Showing {filtered.length} profiles ({experts.filter((e) => !e.isMock).length} live from DB + {mockExperts.length} mock)
              </span>
            </>
          )}
        </div>
        <button
          onClick={fetchLiveExperts}
          disabled={liveLoading}
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono-data text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
          title="Refresh live profiles"
        >
          <RefreshCw size={10} className={liveLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" role="table" aria-label="Expert review queue">
          <thead>
            <tr className="border-b border-border">
              {[
                { label: 'Pseudonym', key: null, width: 'w-36' },
                { label: 'Domain Tags', key: null, width: 'w-56' },
                { label: 'Tier', key: null, width: 'w-24' },
                { label: 'Badge', key: null, width: 'w-24' },
                { label: 'Verification', key: null, width: 'w-32' },
                { label: 'Rating', key: 'averageRating' as SortKey, width: 'w-20' },
                { label: 'Count', key: 'ratingCount' as SortKey, width: 'w-16' },
                { label: 'Rate /hr', key: 'hourlyRate' as SortKey, width: 'w-24' },
                { label: 'Submitted', key: 'submittedAt' as SortKey, width: 'w-24' },
                { label: 'Status', key: null, width: 'w-20' },
                { label: 'CV Ready', key: null, width: 'w-20' },
                { label: 'Source', key: null, width: 'w-16' },
                { label: 'Actions', key: null, width: 'w-40' },
              ].map((col) => (
                <th
                  key={`th-${col.label}`}
                  className={`px-4 py-3 text-left text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground ${col.width} ${col.key ? 'cursor-pointer hover:text-foreground' : ''}`}
                  onClick={() => col.key && handleSort(col.key)}
                  aria-sort={col.key && sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.key && <SortIcon col={col.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-4 py-16 text-center">
                  <p className="text-muted-foreground text-sm">No experts match the current filters.</p>
                </td>
              </tr>
            ) : (
              filtered.map((expert, idx) => (
                <tr
                  key={expert.id}
                  className={`border-b border-border/50 hover:bg-muted/30 transition-colors group ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-sm font-semibold text-foreground">{expert.pseudonym}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {expert.domainTags.slice(0, 2).map((tag) => (
                        <span key={`admin-tag-${expert.id}-${tag}`} className="text-[9px] font-mono-data px-1.5 py-0.5 rounded bg-primary/8 text-primary">
                          {tag}
                        </span>
                      ))}
                      {expert.domainTags.length > 2 && (
                        <span className="text-[9px] font-mono-data px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          +{expert.domainTags.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-xs text-muted-foreground">{expert.experienceTier}</span>
                  </td>
                  <td className="px-4 py-3">
                    <BadgeTierChip tier={expert.badgeTier} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <VerificationBadge
                      status={expert.verificationStatus}
                      webVerifySummary={expert.webVerifySummary}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-sm text-foreground">
                      {expert.averageRating > 0 ? expert.averageRating.toFixed(1) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-sm text-muted-foreground">{expert.ratingCount}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-sm text-foreground">
                      ₹{expert.hourlyRate.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-xs text-muted-foreground">{expert.submittedAt}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono-data text-[10px] px-2 py-0.5 rounded-full ${expert.isLive ? 'bg-secondary/10 text-secondary' : 'bg-muted text-muted-foreground'}`}>
                      {expert.isLive ? 'LIVE' : 'HIDDEN'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="font-mono-data text-sm"
                      title={expert.cvReady ? 'Structured CV available' : 'No structured CV yet'}
                      aria-label={expert.cvReady ? 'CV Ready' : 'CV Not Ready'}
                    >
                      {expert.cvReady ? (
                        <span className="text-secondary">✓</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono-data text-[9px] px-1.5 py-0.5 rounded ${expert.isMock ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'}`}>
                      {expert.isMock ? 'MOCK' : 'LIVE'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {actionLoading === expert.id ? (
                        <Loader2 size={14} className="text-muted-foreground animate-spin" />
                      ) : (
                        <>
                          {expert.verificationStatus !== 'WEB_CHECKED' && expert.verificationStatus !== 'FLAGGED' && (
                            <button
                              onClick={() => handleApprove(expert.id, expert.pseudonym, expert.isMock)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-secondary/10 text-secondary text-xs font-medium hover:bg-secondary/20 active:scale-95 transition-all duration-150"
                              title={`Approve ${expert.pseudonym}`}
                              aria-label={`Approve ${expert.pseudonym}`}
                            >
                              <CheckCircle size={12} />
                              Approve
                            </button>
                          )}
                          {expert.verificationStatus !== 'FLAGGED' && (
                            <button
                              onClick={() => handleFlag(expert.id, expert.pseudonym, expert.isMock)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20 active:scale-95 transition-all duration-150"
                              title={`Flag ${expert.pseudonym}`}
                              aria-label={`Flag ${expert.pseudonym}`}
                            >
                              <Flag size={12} />
                              Flag
                            </button>
                          )}
                          <Link
                            href={`/admin/experts/${expert.id}`}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 active:scale-95 transition-all duration-150"
                            title={`View profile for ${expert.pseudonym}`}
                            aria-label={`View profile for ${expert.pseudonym}`}
                          >
                            <ExternalLink size={12} />
                            View
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}